import { useId, useMemo } from 'react';
import { trpc } from '@/api/trpc';
import { FormCheckbox, ScreenerRuleSelect } from '@/components/UI';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { EntityType } from '@/types/appRouter';

export const SCREENER_UNIVERSE_ENTITY_TYPES = ['Asset', 'Cryptocurrency', 'AssetIndex'] as const;

export type ScreenerUniverseEntityType = (typeof SCREENER_UNIVERSE_ENTITY_TYPES)[number];

interface ScreenerUniverseSelectorProps {
    screenerRuleId: number | null;
    entityTypes: ScreenerUniverseEntityType[];
    onScreenerRuleIdChange: (screenerRuleId: number | null) => void;
    onEntityTypesChange: (entityTypes: ScreenerUniverseEntityType[]) => void;
    error?: string;
    disabled?: boolean;
    selectHelpText?: string;
    selectLabel?: string;
    showEntityTypeFilters?: boolean;
}

function isScreenerUniverseEntityType(entityType: EntityType): entityType is ScreenerUniverseEntityType {
    return SCREENER_UNIVERSE_ENTITY_TYPES.includes(entityType as ScreenerUniverseEntityType);
}

function formatScreenerUniverseEntityType(entityType: ScreenerUniverseEntityType): string {
    return entityType === 'Asset' ? 'Assets' : entityType === 'AssetIndex' ? 'Indexes' : 'Crypto';
}

function formatEntityTypes(entityTypes: readonly EntityType[]): string {
    if (entityTypes.length === 0) return 'Loading types...';
    return entityTypes.join(', ');
}

function sameEntityTypes(left: ScreenerUniverseEntityType[], right: ScreenerUniverseEntityType[]): boolean {
    return left.length === right.length && left.every((entityType) => right.includes(entityType));
}

function getScreenerSelectHelpText(params: { hasNoSupportedEntityTypes: boolean; queryError: unknown }): string | undefined {
    if (params.queryError) return 'Failed to load screener rule details.';
    if (params.hasNoSupportedEntityTypes) return 'Selected rule does not support an AERO or experiment universe entity type.';

    return undefined;
}

function getEntityTypeFilterStatus(params: {
    entityTypes: ScreenerUniverseEntityType[];
    supportedEntityTypes: ScreenerUniverseEntityType[];
}): string {
    if (params.supportedEntityTypes.length === 0) return 'No supported entry entity types';
    if (params.entityTypes.length === 0) return 'All supported entity types';

    return 'Filtered entity types';
}

const ScreenerUniverseSelector = ({
    screenerRuleId,
    entityTypes,
    onScreenerRuleIdChange,
    onEntityTypesChange,
    error,
    disabled = false,
    selectHelpText,
    selectLabel = 'Screener Rule',
    showEntityTypeFilters = true
}: ScreenerUniverseSelectorProps) => {
    const idPrefix = useId();

    const screenerQuery = useAppQuery({
        ...trpc.screener.get.queryOptions({ screenerRuleId: screenerRuleId ?? 0 }),
        enabled: !disabled && screenerRuleId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const ruleEntityTypes = screenerQuery.data?.screener.entityTypes ?? [];

    const supportedEntityTypes = useMemo(() => {
        const filtered = ruleEntityTypes.filter(isScreenerUniverseEntityType);

        return screenerQuery.data ? filtered : [...SCREENER_UNIVERSE_ENTITY_TYPES];
    }, [ruleEntityTypes, screenerQuery.data]);

    const effectiveEntityTypes =
        entityTypes.length > 0 ? entityTypes.filter((entityType) => supportedEntityTypes.includes(entityType)) : supportedEntityTypes;

    const queryError = screenerQuery.error;
    const hasNoSupportedEntityTypes = screenerQuery.data !== undefined && supportedEntityTypes.length === 0;

    return (
        <div className="space-y-3">
            <ScreenerRuleSelect
                error={error}
                helpText={getScreenerSelectHelpText({ hasNoSupportedEntityTypes, queryError }) ?? selectHelpText}
                isDisabled={disabled}
                label={selectLabel}
                value={screenerRuleId}
                onChange={(nextScreenerRuleId) => {
                    onScreenerRuleIdChange(nextScreenerRuleId);
                    onEntityTypesChange([]);
                }}
            />

            {showEntityTypeFilters && screenerRuleId !== null && (
                <ScreenerUniverseEntityTypeFilters
                    disabled={disabled}
                    effectiveEntityTypes={effectiveEntityTypes}
                    entityTypes={entityTypes}
                    idPrefix={idPrefix}
                    ruleEntityTypes={ruleEntityTypes}
                    supportedEntityTypes={supportedEntityTypes}
                    onEntityTypesChange={onEntityTypesChange}
                />
            )}
        </div>
    );
};

const ScreenerUniverseEntityTypeFilters = ({
    disabled,
    effectiveEntityTypes,
    entityTypes,
    idPrefix,
    ruleEntityTypes,
    supportedEntityTypes,
    onEntityTypesChange
}: {
    disabled: boolean;
    effectiveEntityTypes: ScreenerUniverseEntityType[];
    entityTypes: ScreenerUniverseEntityType[];
    idPrefix: string;
    ruleEntityTypes: EntityType[];
    supportedEntityTypes: ScreenerUniverseEntityType[];
    onEntityTypesChange: (entityTypes: ScreenerUniverseEntityType[]) => void;
}) => {
    const toggleEntityType = (entityType: ScreenerUniverseEntityType, checked: boolean) => {
        const next = checked
            ? [...effectiveEntityTypes, entityType]
            : effectiveEntityTypes.filter((currentEntityType) => currentEntityType !== entityType);

        const normalized = supportedEntityTypes.filter((supportedEntityType) => next.includes(supportedEntityType));

        if (normalized.length === 0) return;

        onEntityTypesChange(sameEntityTypes(normalized, supportedEntityTypes) ? [] : normalized);
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-zinc-200">Entity Types</div>
                <div className="text-xs text-zinc-500">Rule: {formatEntityTypes(ruleEntityTypes)}</div>
            </div>
            <div className="flex flex-wrap gap-4">
                {SCREENER_UNIVERSE_ENTITY_TYPES.map((entityType) => {
                    const supported = supportedEntityTypes.includes(entityType);

                    return (
                        <FormCheckbox
                            checked={supported && effectiveEntityTypes.includes(entityType)}
                            disabled={disabled || !supported}
                            id={`${idPrefix}-screener-universe-entity-type-${entityType}`}
                            key={entityType}
                            label={formatScreenerUniverseEntityType(entityType)}
                            labelClassName={supported ? 'text-zinc-300' : 'text-zinc-600'}
                            onChange={(event) => toggleEntityType(entityType, event.target.checked)}
                        />
                    );
                })}
            </div>
            <div className="mt-2 text-xs text-zinc-500">{getEntityTypeFilterStatus({ entityTypes, supportedEntityTypes })}</div>
        </div>
    );
};

export default ScreenerUniverseSelector;
